import { injectable, inject } from "inversify";
import { ErrorSeverity, ThrowErrorFromResponseFunction, throwErrorFromResponseRTTI } from "~/modules/error";
import { sessionRTTI, Session } from "~/modules/session";
import { HttpAdapter } from "./HttpAdapter";
import { EnvironmentConfig, environmentConfigRTTI } from "~/modules/config";
import { mapRootKeysToCamelCase, mapRootKeysToSnakeCase } from "~/utils/mapKeys";
import { extractFileNameFromPath } from "~/utils/extractFromPath";

@injectable()
export class HttpAdapterImpl implements HttpAdapter {
    private readonly session: Session;

    private readonly envConfig: EnvironmentConfig;

    readonly #throwErrorFromResponse: ThrowErrorFromResponseFunction;

    constructor(
        @inject(sessionRTTI) session: Session,
        @inject(environmentConfigRTTI) envConfig: EnvironmentConfig,
        @inject(throwErrorFromResponseRTTI) throwErrorFromResponse: ThrowErrorFromResponseFunction
    ) {
        this.session = session;
        this.envConfig = envConfig;
        this.#throwErrorFromResponse = throwErrorFromResponse;
    }

    public get<T>(url: string, authMethod?: string): Promise<T> {
        return this.performNetworkCall<T>(url, "GET", authMethod);
    }

    public post<T>(url: string, authMethod?: string, body?: {}): Promise<T> {
        return this.performNetworkCall<T>(url, "POST", authMethod, body);
    }

    public put<T>(url: string, authMethod?: string, body?: {}): Promise<T> {
        return this.performNetworkCall<T>(url, "PUT", authMethod, body);
    }

    public delete<T>(url: string, authMethod?: string): Promise<T> {
        return this.performNetworkCall<T>(url, "DELETE", authMethod);
    }

    private getRequestData(method: string, authMethod?: string, body?: object): object {
        let headers: Headers | undefined;
        if (authMethod === "jwe") {
            headers = new Headers({
                Authorization: `Basic ${btoa(`token:${this.session.token}`)})`,
                "Content-Type": "application/json"
            });
        }
        return { headers, method, body: JSON.stringify(body) };
    }

    private getEnvironmentSpecificUrl(url: string): string {
        let urlEnvSpecifier = "";
        const region = this.envConfig.region || "";
        if (region.startsWith("dev-")) {
            urlEnvSpecifier = ".dev";
        } else if (region.startsWith("stage-")) {
            urlEnvSpecifier = ".stage";
        }
        return url.replace("[environment]", urlEnvSpecifier);
    }

    private mapBody(body: object | undefined): object | undefined {
        if (body) {
            return mapRootKeysToSnakeCase(body);
        }
        return body;
    }

    private async performNetworkCall<T>(url: string, method: string, authMethod?: string, body?: object): Promise<T> {
        const environmentSpecificUrl = this.getEnvironmentSpecificUrl(url);
        const response: Response = await fetch(
            environmentSpecificUrl,
            this.getRequestData(method, authMethod, this.mapBody(body))
        );
        if (!response.ok) {
            const metadata = {
                module: "backend",
                severity: ErrorSeverity.Error,
                source: extractFileNameFromPath(__filename)
            };
            await this.#throwErrorFromResponse(response, metadata);
        }
        const result = await response.json();

        return mapRootKeysToCamelCase(result) as Promise<T>;
    }
}
